import authenticatedAxios from '@/lib/axios';
import { USE_MOCK_DATA } from '@/constants';
import { ApiManagedUserScoreProfile, ApiManagerTeamSummary } from '@/types/api';
import { cachedGet } from './cache';

/**
 * Get scorecard summary for a manager's team.
 *
 * Get a comprehensive summary of scorecards for all users reporting to
 * a given manager, grouped by certification.
 *
 * Includes:
 *  - Certification and scenario short titles for better UI display
 *  - Most recent activity timestamp across all session types
 *  - Attempt counts for each session type
 */
export const getManagerSummary = async (managerId: string): Promise<ApiManagerTeamSummary> => {
  if (USE_MOCK_DATA) {
    /**
     * @todo Replace with realistic mock data of ApiManagerTeamSummary
     */
    return {
      manager_id: 'mock_manager_id',
      manager_name: 'Mock Manager',
      products: [],
    };
  }

  const endpoint = `/app/scorecards/manager/${managerId}/products/summary`;
  return cachedGet(`manager.summary:${managerId}`, async () => {
    const response = await authenticatedAxios.get<ApiManagerTeamSummary>(endpoint);
    return response.data;
  }, 2 * 60 * 1000);
};

/**
 * Get scorecards shared with a manager
 *
 * Get all scorecards that have been shared with a specific manager,
 * organized by certification and grouped by the users who shared them.
 *
 * Categories include:
 *  - certificate: Certification attempts (`is_certification: true`, `is_practice: false`)
 *  - practice_certificate: Practice sessions (`is_certification: false`, `is_practice: true`)
 *  - roleplay: Roleplay sessions (`is_certification: false`, `is_practice: false`)
 */
export const getManagerSharedScorecards = async (
  managerId: string,
): Promise<any[]> => {
  if (USE_MOCK_DATA) {
    return [];
  }

  const endpoint = `/app/scorecards/manager/${managerId}/products/shared`;
  return cachedGet(`manager.shared:${managerId}`, async () => {
    const response = await authenticatedAxios.get<any[]>(endpoint);
    return response.data;
  }, 2 * 60 * 1000);
};

/**
 * Get scorecards shared with a manager for a specific user
 *
 * Similar to `getManagerSharedScorecards`, but filters the shared scorecards to only those
 * shared by a specific user. This is useful for viewing the shared scorecards of an
 * individual team member. The response structure is the same, but the results are
 * scoped to the specified user.
 */
export const getManagerSharedScorecardsByUserId = async (
  managerId: string,
  userId: string,
): Promise<any[]> => {
  if (USE_MOCK_DATA) {
    return [];
  }

  const endpoint = `/app/scorecards/manager/${managerId}/shared?user_id=${userId}`;
  const response = await authenticatedAxios.get<any[]>(endpoint);
  return response.data;
};

/**
 * Get detailed scorecard profile for a specific user under a manager
 *
 * Retrieve a detailed profile of a user's scorecards that are shared with their manager.
 * This includes all scorecards shared by the user, along with detailed information about
 * each scorecard, such as scores, attempt history, and timestamps. This endpoint is useful
 * for managers who want to drill down into the performance of an individual team member.
 */
export const getManagerUserProfile = async (
  managerId: string,
  userId: string,
): Promise<ApiManagedUserScoreProfile> => {
  if (USE_MOCK_DATA) {
    return {} as ApiManagedUserScoreProfile;
  }

  const endpoint = `/app/scorecards/manager/${managerId}/user/${userId}/products/profile`;
  const response = await authenticatedAxios.get<ApiManagedUserScoreProfile>(endpoint);
  return response.data;
};
